
import { computed, defineComponent, PropType, reactive, ref, watch } from 'vue'
import LabelBox from '@/pages/Products/components/LabelBox.vue'
import { CtProp, CtStockItem } from '@/models/interfaces/content-type'
import EditableBlockInput from '@/components/EditableBlockInput.vue'
import DButton from '@/components/base/DButton.vue'
import TickIcon from '@/components/Icons/TickIcon.vue'
import CloseIcon from '@/components/Icons/CloseIcon.vue'
import AddIcon from '@/components/Icons/AddIcon.vue'
import { generateUUID, UUID } from '@Protium/vue-ozone-utils'
import { cloneDeep, debounce, kebabCase } from 'lodash'
import TrashIcon from '@/components/Icons/TrashIcon.vue'

export default defineComponent({
  name: 'PropertyInfoItem',
  components: { AddIcon, CloseIcon, TickIcon, DButton, EditableBlockInput, LabelBox, TrashIcon },
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    propId: {
      type: String as PropType<UUID>,
      required: true,
    },
    propValue: {
      type: String,
      required: true,
    },
    currentProps: {
      type: Object as PropType<CtStockItem['props']>,
    },
    allProps: {
      type: Array as PropType<CtProp[]>,
      default: () => []
    },
    propValues: {
      type: Array as PropType<Array<string>>,
      default: () => []
    }
  },
  emits: ['update:prop-id', 'update:prop-value', 'update:new-prop', 'remove'],
  expose: ['newPropMode', 'newProp'],
  setup (props, { emit }) {
    const originalPropId = ref(cloneDeep(props.propId))

    const propName = computed(() => {
      const prop = props.allProps.find(p => p.sys.id === props.propId)
      if (!prop) return ''
      return prop?.name ?? prop?.id ?? prop.sys.id
    })

    const proptions = computed(() => props.allProps
      .filter(p => !(p.sys.id in (props.currentProps ?? {})) || p.sys.id === props.propId)
      .map(p => ({
        label: p?.name ?? p.id ?? p.sys.id,
        value: p.sys.id,
      }))
    )

    const activeProption = computed({
      get: () => proptions.value.find((op) => op.value === props.propId),
      set: (newValue) => {
        originalPropId.value = newValue?.value
        emit('update:prop-id', newValue?.value)
      }
    })

    const propValueOptions = computed(
      () => props.propValues.filter(Boolean).map(pv => ({ label: pv, value: pv }))
    )

    const newPropMode = ref(false)
    const newProp = reactive({
      id: computed(() => kebabCase(newProp.name)),
      value: '',
      name: '',
      type: 'default',
      sys: {
        id: generateUUID(),
        draft: true,
      }
    })
    watch(() => newProp.name, debounce(() => emit('update:new-prop', newProp), 500))

    const setNewPropMode = () => {
      if (!props.propId) return
      newPropMode.value = !props.allProps.find(ap => ap.sys.id === props.propId)
      newProp.sys.id = props.propId
    }
    setNewPropMode()
    watch(() => [props.propId, ...props.allProps], setNewPropMode)

    watch(() => newPropMode.value, (newValue) => {
      if (newValue) {
        emit('update:prop-id', newProp.sys.id)
      } else {
        emit('update:prop-id', originalPropId.value)
      }
    })

    const allPropIds = computed(() => props.allProps.map(p => p.sys.id))

    const allPropsMapping = computed(
      () => props.allProps?.reduce((prev, cur) => ({ ...prev, [cur.sys.id]: cur}), {})
    )

    const fastPropValue = ref(props.propValue)
    watch(() => props.propValue, (newValue) => fastPropValue.value = newValue)

    const updatePropValue = debounce((event) => {
      emit('update:prop-value', event)
    }, 500)

    return {
      propName,
      proptions,
      activeProption,
      newPropMode,
      newProp,
      allPropIds,
      allPropsMapping,
      propValueOptions,
      fastPropValue,
      updatePropValue,
    }
  }
})
